import LegalLayout, { LegalSection } from '@/components/LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Milk collects, uses, discloses, and protects your personal information — in line with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws."
      updated="July 2026"
    >
      <LegalSection id="intro" title="1. Who we are">
        <p>
          Milk ("Milk", "we", "us", or "our") provides a conversation-capture and follow-up
          assistant available at addmilk.ai (the "Service"). We are committed to protecting the
          privacy of your personal information in accordance with PIPEDA and, where applicable,
          provincial legislation including Quebec's Law 25, British Columbia's PIPA, and Alberta's PIPA.
        </p>
        <p>
          This Policy explains what personal information we collect, why we collect it, how we use
          and safeguard it, and the choices and rights available to you.
        </p>
      </LegalSection>

      <LegalSection id="accountability" title="2. Accountability">
        <p>
          Milk is accountable for personal information under its control. We have designated a Privacy
          Officer responsible for our compliance with this Policy and applicable law. You can reach our
          Privacy Officer at <a href="mailto:privacy@addmilk.ai" style={{ color: '#4C73A6' }}>privacy@addmilk.ai</a>.
        </p>
      </LegalSection>

      <LegalSection id="collection" title="3. Information we collect">
        <p>We collect only the personal information needed to provide the Service:</p>
        <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li><strong>Account information</strong> — your name, email address, and profile details, obtained when you sign in with Google or Microsoft.</li>
          <li><strong>Conversation content you choose to capture</strong> — audio recordings, pasted text, meeting transcripts, summaries, decisions, tasks, and contacts you create.</li>
          <li><strong>Connected-account data</strong> — limited access tokens that let you send email and create calendar events from your own Google or Microsoft account, only when you ask Milk to act.</li>
          <li><strong>Usage and device information</strong> — log data such as IP address, browser type, and feature usage, used to operate and secure the Service.</li>
        </ul>
      </LegalSection>

      <LegalSection id="purposes" title="4. Purposes &amp; consent">
        <p>
          We identify the purposes for which personal information is collected at or before the time of
          collection, and we obtain your consent. We use your information to: create and manage your
          account; transcribe and summarize conversations you capture; generate follow-up emails and
          calendar reminders at your direction; provide support; and maintain the security and integrity
          of the Service.
        </p>
        <p>
          You may withdraw your consent at any time, subject to legal or contractual restrictions and
          reasonable notice, by contacting us or deleting your account. Withdrawing consent may limit
          our ability to provide certain features.
        </p>
      </LegalSection>

      <LegalSection id="use-disclosure" title="5. Limiting use, disclosure &amp; retention">
        <p>
          We use and disclose personal information only for the purposes identified above, or as
          permitted or required by law. We do not sell your personal information. We retain personal
          information only as long as necessary to fulfil those purposes or to meet legal requirements,
          after which it is securely deleted or anonymized.
        </p>
        <p>
          We engage trusted service providers (for example, transcription, AI processing, email delivery,
          and cloud hosting) who process information on our behalf under contractual confidentiality and
          security obligations. Some providers may store or process data outside Canada (including the
          United States); where this occurs, the information may be subject to the laws of those
          jurisdictions, and we take reasonable steps to ensure comparable protection.
        </p>
      </LegalSection>

      <LegalSection id="residency" title="6. Data residency &amp; storage regions">
        <p>
          Where your personal information is stored depends on the country you select during
          onboarding. We map your country to the closest available hosting region and store your
          account and conversation data there:
        </p>
        <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li><strong>Canada</strong> — hosted in the Canada (Central) region (<span style={{ fontFamily: 'monospace' }}>ca-central-1</span>).</li>
          <li><strong>United States</strong> — hosted in the US East (Ohio) region (<span style={{ fontFamily: 'monospace' }}>us-east-2</span>).</li>
          <li><strong>European Economic Area, Switzerland &amp; the United Kingdom</strong> — hosted in the EU (Frankfurt) region (<span style={{ fontFamily: 'monospace' }}>eu-central-1</span>).</li>
          <li><strong>All other countries</strong> — hosted in our default region, the United States (<span style={{ fontFamily: 'monospace' }}>us-east-2</span>).</li>
        </ul>
        <p>
          Your home region is set once, at signup, and is locked thereafter to preserve data
          residency. If you need your data moved to a different region, contact our Privacy Officer.
          Regardless of storage region, a limited number of our service providers (for example AI
          processing and email delivery) may process data in other jurisdictions, including the
          United States, as described in Section 5.
        </p>
      </LegalSection>

      <LegalSection id="ai" title="7. AI processing">
        <p>
          Conversation content you capture is processed by automated and AI systems to produce
          transcripts, summaries, action items, and draft communications. We do not use your private
          conversation content to train third-party foundation models, and access by our service
          providers is limited to what is necessary to perform the service.
        </p>
      </LegalSection>

      <LegalSection id="safeguards" title="8. Safeguards">
        <p>
          We protect personal information with safeguards appropriate to its sensitivity, including
          encryption in transit, access controls, authentication, and secure storage of connected-account
          tokens. No method of transmission or storage is perfectly secure, but we work continuously to
          protect your information against loss, theft, and unauthorized access.
        </p>
      </LegalSection>

      <LegalSection id="access" title="9. Accuracy, access &amp; correction">
        <p>
          We keep personal information as accurate, complete, and up to date as necessary. You have the
          right to access the personal information we hold about you and to request corrections. Most of
          your information can be viewed and edited directly in the Service. To make a formal access or
          correction request, contact our Privacy Officer; we will respond within the timeframes required
          by law.
        </p>
      </LegalSection>

      <LegalSection id="openness" title="10. Openness &amp; challenging compliance">
        <p>
          This Policy is part of our commitment to openness about our information practices. If you have a
          question or concern about how we handle your personal information, please contact our Privacy
          Officer. If we are unable to resolve your concern, you have the right to contact the Office of
          the Privacy Commissioner of Canada, or your applicable provincial privacy regulator.
        </p>
      </LegalSection>

      <LegalSection id="children" title="11. Children">
        <p>
          The Service is not directed to individuals under the age of majority in their province or
          territory, and we do not knowingly collect their personal information.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="12. Changes to this Policy">
        <p>
          We may update this Policy from time to time. Material changes will be posted on this page with
          an updated effective date, and where required, we will notify you.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="13. Contact us">
        <p>
          Milk — Privacy Officer<br />
          Email: <a href="mailto:privacy@addmilk.ai" style={{ color: '#4C73A6' }}>privacy@addmilk.ai</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
